import axios from 'axios';
import { SubmissionError } from 'redux-form';
import { t } from 'i18next';
import qs from 'qs';
import { endpoints, RLAccountNumber } from '../../utils/constants';
import {
  getAddress, getZip, getCity, getState, getCountry, getCountryFromZip, getSingleAddress, transformCountryToCode, serviceLevelCodeSelection,
} from '../../utils/func';
import handleDeliveryType from '../../utils/deliveryAddress';
import {
  PaymentMethods, PaymentMethodsShipment, PrefillNumberTypes, BillToMethods, SamedayShipmentType,
} from '../../utils/enums';
import { parseAttachedDocuments } from '../../utils/uploadedFiles';
import { accessorialsLists, measurementLists, getIsResidential } from '../../utils/orders';

const getPaymentTypeForApi = (paymentMethod, billToDayAndRoss) => {
  if (billToDayAndRoss === false) {
    return PaymentMethodsShipment.Other;
  }

  switch (paymentMethod) {
    case PaymentMethods.CreditCard:
      return PaymentMethodsShipment.CreditCard;
    case PaymentMethods.Shipper:
      return PaymentMethodsShipment.Shipper;
    case PaymentMethods.Receiver:
      return PaymentMethodsShipment.Receiver;
    case PaymentMethods.Other:
      return PaymentMethodsShipment.Other;
    default:
      return PaymentMethodsShipment.CreditCard;
  }
};

const formValuesToAPI = (formData, serviceLevelCode, userId, userEmail, userName, isAuthenticated) => {
  const {
    shipmentId,
    division,
    paymentMethod,
    shipperContactName,
    shipperCompanyName,
    shipperAddress,
    shipperAddressAdditional,
    shipperEmail,
    shipperCity,
    shipperPostalZipCode,
    shipperProvinceState,
    shipperCountry,
    shipperPhoneNumber,
    shipperPhoneNumberExt,
    shipperMobileSMSNotification,
    shipperResidentialAddress,
    consigneeAddressAdditional,
    consigneeEmail,
    consigneeCity,
    consigneePhoneNumber,
    consigneeAddress,
    consigneeContactName,
    consigneeCompanyName,
    consigneePhoneNumberExt,
    consigneeMobileSMSNotification,
    consigneeResidentialAddress,
    consigneePostalZipCode,
    consigneeProvinceState,
    consigneeCountry,
    measurementType,
    measurementList,
    accessorials,
    quoteDest,
    pickupDate,
    emergencyPhone,
    erapReference,
    erapPhone,
    dgContactName,
    referenceNumber,
    poNumber,
    specialInstructions,
    readyTime,
    closingTime,
    shipmentType,
    accessByTrailer,
    droppingFreightOff,
    regularScheduledPickup,
    attachedDocuments,
    billingAddressAdditional,
    billingEmail,
    billingPhoneNumber,
    billingAddress,
    billingContactName,
    billingCompanyName,
    billingPhoneNumberExt,
    gstNumber,
    quoteOrShipmentNumber,
    copyOrderID,
    billToMethod,
    billToDayAndRoss,
    billTo,
    thirdPartyAccountNumber,
    numberTypeSelected,
    selectedUnits,
    sendConsigneeInfo,
    returnAuthorization,
    isQuote,
    broker,
    createBillOfLading,
  } = formData;

  const shipperAddress1 = getSingleAddress(shipperAddress);
  const shipperState = shipperProvinceState;
  const shipperZip = shipperPostalZipCode;
  const shipperCountryValue = shipperCountry ? transformCountryToCode(shipperCountry) : getCountryFromZip(shipperPostalZipCode);

  const consigneeAddress1 = getSingleAddress(consigneeAddress);
  const consigneeState = consigneeProvinceState;
  const consigneeZip = consigneePostalZipCode;
  const consigneeCountryValue = consigneeCountry ? transformCountryToCode(consigneeCountry) : getCountryFromZip(consigneePostalZipCode);

  const DayAndRossBillTo = (billToMethod !== BillToMethods.enterBillTo) ? billTo : thirdPartyAccountNumber;

  const isShipperResidential = getIsResidential(shipperResidentialAddress, accessorials, 'PRESPU');
  const isConsigneeResidential = getIsResidential(consigneeResidentialAddress, accessorials, 'PRESDL');

  const brokerName = broker && broker.name ? broker.name : '';
  const brokerEmail = broker && broker.emailAddress ? broker.emailAddress : '';
  const brokerPhoneNumber = broker && broker.phoneNumber ? broker.phoneNumber : '';

  const formValueToApi = {
    shipmentId,
    accountNumber: (billToDayAndRoss === false) ? RLAccountNumber : DayAndRossBillTo,
    createByUserId: userId,
    createByUserEmail: userEmail,
    caller: isAuthenticated ? { name: userName, contact: userName, phone: shipperPhoneNumber } : { name: 'WEBSITE GUEST', contact: 'WEBSITE GUEST' },
    paymentType: (!isAuthenticated && !!quoteOrShipmentNumber)
      ? PaymentMethodsShipment.Other
      : getPaymentTypeForApi(paymentMethod, billToDayAndRoss),
    language: 'EN',
    division: division || 'Sameday',
    originalShipment: quoteOrShipmentNumber,
    copyOrderID,
    from: {
      companyName: shipperCompanyName,
      contactName: shipperContactName,
      address1: shipperAddress1,
      address2: shipperAddressAdditional || '',
      city: shipperCity,
      provinceCode: shipperState,
      countryCode: shipperCountryValue,
      postalCode: shipperZip,
      contactPhoneNumber: shipperPhoneNumber,
      contactPhoneNumberExt: shipperPhoneNumberExt || '',
      cellNumber: shipperMobileSMSNotification || '',
      contactEmail: shipperEmail,
      isResidential: isShipperResidential,
    },
    to: {
      companyName: consigneeCompanyName,
      contactName: consigneeContactName,
      address1: consigneeAddress1,
      address2: consigneeAddressAdditional || '',
      city: consigneeCity,
      provinceCode: consigneeState,
      countryCode: consigneeCountryValue,
      postalCode: consigneeZip,
      contactPhoneNumber: consigneePhoneNumber,
      contactPhoneNumberExt: consigneePhoneNumberExt || '',
      cellNumber: consigneeMobileSMSNotification || '',
      contactEmail: consigneeEmail,
      isResidential: isConsigneeResidential,
    },
    accessorials: accessorialsLists(accessorials),
    shipmentItems: measurementLists(measurementList, measurementType.value, isQuote),
    shipmentType: shipmentType || SamedayShipmentType.Regular,
    quoteNumber: numberTypeSelected.value === PrefillNumberTypes.quote ? quoteOrShipmentNumber : undefined,
    confirmationEmail: quoteDest,
    measurementType: (selectedUnits.indexOf(t('BtnImperial')) > -1) ? 1 : 0,
    specialInstructions: specialInstructions || '',
    droppingFreightOff: droppingFreightOff ? droppingFreightOff.value : false,
    hasRegularPickups: regularScheduledPickup ? regularScheduledPickup.value : false,
    isAllowAccessByTrailer: accessByTrailer.value,
    pickupDetails: {
      pickupDate,
      readyTime: (readyTime && readyTime.hours && readyTime.minutes && readyTime.amOrPm)
        ? `${readyTime.hours.length < 2 ? `0${readyTime.hours}` : readyTime.hours}:${readyTime.minutes.length < 2
          ? `0${readyTime.minutes}` : readyTime.minutes} ${readyTime.amOrPm}`
        : null,
      closingTime: (closingTime && closingTime.hours && closingTime.minutes && closingTime.amOrPm)
        ? `${closingTime.hours.length < 2 ? `0${closingTime.hours}` : closingTime.hours}:${closingTime.minutes.length < 2
          ? `0${closingTime.minutes}` : closingTime.minutes} ${closingTime.amOrPm}`
        : null,
    },
    serviceLevelCode: serviceLevelCode || '',
    referenceNumber: referenceNumber || '',
    poNumber: poNumber || '',
    emergencyPhoneNumber: emergencyPhone || '',
    erapReferenceNumber: erapReference || '',
    erapPhoneNumber: erapPhone || '',
    contactName: dgContactName || '',
    sendEmailToConsignee: sendConsigneeInfo,
    thirdPartyDetails: paymentMethod === PaymentMethods.ThirdParty ? {
      companyName: billingCompanyName,
      contactName: billingContactName,
      address1: getAddress(billingAddress),
      address2: billingAddressAdditional,
      city: getCity(billingAddress),
      provinceCode: getState(billingAddress),
      countryCode: getCountry(billingAddress),
      postalCode: getZip(billingAddress),
      contactPhoneNumber: billingPhoneNumber,
      contactPhoneNumberExt: billingPhoneNumberExt,
      contactEmail: billingEmail,
    } : {
      companyName: '',
      contactName: '',
      address1: '',
      address2: '',
      city: '',
      provinceCode: '',
      countryCode: '',
      postalCode: '',
      contactPhoneNumber: '',
      contactPhoneNumberExt: '',
      contactEmail: '',
      cellNumber: '',
    },
    gstNumber: gstNumber || '',
    thirdPartyAccountNumber: thirdPartyAccountNumber || '',
    returnAuthorization,
    brokerName,
    brokerEmail,
    brokerPhoneNumber,
    isBolRequested: createBillOfLading === true,
  };

  parseAttachedDocuments(attachedDocuments, formValueToApi);

  return formValueToApi;
};

export const apiGetOrderById = (probillNumber) =>
  axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.getOrderById}`, {
    params: {
      probillNumber,
    },
  });

export const apiTrackOrderById = (probillNumber) =>
  axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.trackOrderById}`, {
    params: {
      probillNumber,
    },
  });

export const apiDuplicateByBillNumber = (billNumber) =>
  axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.duplicateByBillNumber}`, {
    params: {
      billNumber,
    },
  });

export const apiGetOrdersAll = (
  accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate, pageSize, pageNumber, orderBy, sortDirection, search,
) => axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.getOrdersAll}`, {
  params: {
    search,
    accountNumbers,
    statusCodes,
    serviceLevels,
    serviceTypes,
    fromDate,
    toDate,
    pageSize,
    pageNumber,
    orderBy,
    sortDirection,
  },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
});

export const apiGetOrdersInProgress = (
  accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate, pageSize, pageNumber, orderBy, sortDirection, search,
) => axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.getOrdersInProgress}`, {
  params: {
    search,
    accountNumbers,
    statusCodes,
    serviceLevels,
    serviceTypes,
    fromDate,
    toDate,
    pageSize,
    pageNumber,
    orderBy,
    sortDirection,
  },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
});

export const apiGetOrdersCompleted = (
  accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate, pageSize, pageNumber, orderBy, sortDirection, search,
) => axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.getOrdersCompleted}`, {
  params: {
    search,
    accountNumbers,
    statusCodes,
    serviceLevels,
    serviceTypes,
    fromDate,
    toDate,
    pageSize,
    pageNumber,
    orderBy,
    sortDirection,
  },
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
});

export const apiCancelOrder = (shipperZip, orderNumber) => {
  const formValueToApi = {
    orderId: Number(orderNumber),
    zone: shipperZip,
  };

  return axios.post(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.cancel}`, formValueToApi).then((res) => {
    if (res.status === 200 || res.status === 201) {
      window.scrollTo(0, 0);
      return {
        hasReturnedError: false,
        message: t('CancelSuccessMessage'),
      };
    }

    return undefined;
  }).catch(() => ({
    hasReturnedError: true,
    message: t('CancelErrorMessage'),
  }));
};

export const apiGetOrderReports = (accountNumbers, fromDate, toDate, columns) =>
  axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.downloadReport}`, {
    params: {
      accountNumbers,
      fromDate,
      toDate,
      columns,
    },
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

export const apiGetRates = ({
  putReduxServiceLevelQuotes,
  putReduxServiceLevelCode,
  putReduxServiceLevelApproved,
  serviceLevelCode,
  formData,
  userId,
  userEmail,
  userName,
  prevStep,
  isQuoteForm,
  isAuthenticated,
  isShipmentForm,
}) => {
  const { copyOrderID, deliveryType, shipperPhoneNumber } = formData;
  const formValuesAPI = formValuesToAPI(formData, serviceLevelCode, userId, userEmail, userName, isAuthenticated);
  formValuesAPI.copyOrderID = copyOrderID;
  formValuesAPI.caller = isAuthenticated
    ? {
      name: userName, contact: userName, phone: shipperPhoneNumber, clientId: isShipmentForm ? null : formValuesAPI.accountNumber,
    }
    : {
      name: 'WEBSITE GUEST', contact: 'WEBSITE GUEST', phone: '999-999-9999', clientId: null,
    };
  return axios.post(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.getRates}`, formValuesAPI).then((res) => {
    if (res.status === 200) {
      putReduxServiceLevelQuotes(res.data.serviceLevels);
      putReduxServiceLevelCode(handleDeliveryType(deliveryType, res));

      if (isQuoteForm) {
        let atLeastOneApproved = false;
        res.data.serviceLevels.map((item) => {
          if (item.isApproved === true) {
            atLeastOneApproved = true;
          }

          return null;
        });
        putReduxServiceLevelApproved(atLeastOneApproved);
      }
    }
  }).catch((error) => {
    if (error.response.status !== 500 && error.response.status !== 400) {
      prevStep();
      if (error && error.response && error.response.data) {
        const errorMessage = error.response.data.split('\n')[0];
        const sepIndex = errorMessage.indexOf(' ');
        if (sepIndex > 0 && errorMessage.length > sepIndex) {
          throw new SubmissionError({ message: JSON.parse(errorMessage.substring(sepIndex + 1)) });
        }
      }

      throw new SubmissionError({});
    }
  });
};

export const apiCreateOrder = ({
  serviceLevelCode, formData, userId, userEmail, userName, isAuthenticated, updateState, paymentCallback, prevStep, isShipmentForm, aCharges,
}) => {
  const {
    copyOrderID, overrideShipper, overrideConsignee, shipperPhoneNumber,
  } = formData;
  const serviceLevels = serviceLevelCodeSelection(formData);
  const formValuesAPI = formValuesToAPI(formData, serviceLevelCode, userId, userEmail, userName, isAuthenticated);
  formValuesAPI.copyOrderID = copyOrderID;
  const defaultServiceLevelCode = Array.isArray(serviceLevels) ? serviceLevels[0].code : serviceLevels.code;
  formValuesAPI.serviceLevelCode = serviceLevelCode || defaultServiceLevelCode || formData.serviceLevelCode;

  if (aCharges !== undefined) {
    formValuesAPI.aCharges = aCharges;
  }

  formValuesAPI.caller = isAuthenticated
    ? {
      name: userName, contact: userName, phone: shipperPhoneNumber, clientId: isShipmentForm ? null : formValuesAPI.accountNumber,
    }
    : {
      name: 'WEBSITE GUEST', contact: 'WEBSITE GUEST', phone: '999-999-9999', clientId: null,
    };
  if (overrideShipper) {
    formValuesAPI.overrideShipper = {
      companyName: overrideShipper.companyName,
      contactName: overrideShipper.contactName,
      address1: overrideShipper.address1,
      address2: overrideShipper.address2 || '',
      city: overrideShipper.city,
      provinceCode: overrideShipper.provinceCode,
      countryCode: overrideShipper.countryCode,
      postalCode: overrideShipper.postalCode,
      contactPhoneNumber: overrideShipper.phoneNumber,
      contactPhoneNumberExt: overrideShipper.phoneNumberExtension || '',
      cellNumber: overrideShipper.mobileNumber && overrideShipper.mobileNumber !== 0 ? overrideShipper.mobileNumber : '',
      contactEmail: overrideShipper.emailAddress,
    };
  }

  if (overrideConsignee) {
    formValuesAPI.overrideConsignee = {
      companyName: overrideConsignee.companyName,
      contactName: overrideConsignee.contactName,
      address1: overrideConsignee.address1,
      address2: overrideConsignee.address2 || '',
      city: overrideConsignee.city,
      provinceCode: overrideConsignee.provinceCode,
      countryCode: overrideConsignee.countryCode,
      postalCode: overrideConsignee.postalCode,
      contactPhoneNumber: overrideConsignee.phoneNumber,
      contactPhoneNumberExt: overrideConsignee.phoneNumberExtension || '',
      cellNumber: overrideConsignee.mobileNumber && overrideConsignee.mobileNumber !== 0 ? overrideConsignee.mobileNumber : '',
      contactEmail: overrideConsignee.emailAddress,
    };
  }

  return axios.post(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.create}`, formValuesAPI).then((res) => {
    if (res.status === 200 || res.status === 201) {
      updateState('data', {
        ...res.data,
        orderNumber: res.data.orderId,
        currencyCode: res.data.totalAmount.currencyCode,
      }, paymentCallback);
      window.scrollTo(0, 0);
    }
  }).catch((error) => {
    prevStep();

    if (error && error.response && error.response.data) {
      const errorMessage = error.response.data.split('\n')[0];
      const sepIndex = errorMessage.indexOf(' ');
      if (sepIndex > 0 && errorMessage.length > sepIndex) {
        throw new SubmissionError({ message: JSON.parse(errorMessage.substring(sepIndex + 1)) });
      }
    }

    throw new SubmissionError({});
  });
};

export const apiDuplicateOrder = (probillNumber) =>
  axios.get(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.duplicate}`, {
    params: {
      probillNumber,
    },
  });

export const apiUpdateOrder = ({
  serviceLevelCode, formData, userId, userEmail, userName, isAuthenticated, updateState, paymentCallback,
}) => {
  const { overrideShipper, overrideConsignee } = formData;
  const formValuesAPI = formValuesToAPI(formData, serviceLevelCode, userId, userEmail, userName, isAuthenticated);

  if (overrideShipper) {
    formValuesAPI.overrideShipper = {
      companyName: overrideShipper.companyName,
      contactName: overrideShipper.contactName,
      address1: overrideShipper.address1,
      address2: overrideShipper.address2 || '',
      city: overrideShipper.city,
      provinceCode: overrideShipper.provinceCode,
      countryCode: overrideShipper.countryCode,
      postalCode: overrideShipper.postalCode,
      contactPhoneNumber: overrideShipper.phoneNumber,
      contactPhoneNumberExt: overrideShipper.phoneNumberExtension || '',
      cellNumber: overrideShipper.mobileNumber && overrideShipper.mobileNumber !== 0 ? overrideShipper.mobileNumber : '',
      contactEmail: overrideShipper.emailAddress,
    };
  }

  if (overrideConsignee) {
    formValuesAPI.overrideConsignee = {
      companyName: overrideConsignee.companyName,
      contactName: overrideConsignee.contactName,
      address1: overrideConsignee.address1,
      address2: overrideConsignee.address2 || '',
      city: overrideConsignee.city,
      provinceCode: overrideConsignee.provinceCode,
      countryCode: overrideConsignee.countryCode,
      postalCode: overrideConsignee.postalCode,
      contactPhoneNumber: overrideConsignee.phoneNumber,
      contactPhoneNumberExt: overrideConsignee.phoneNumberExtension || '',
      cellNumber: overrideConsignee.mobileNumber && overrideConsignee.mobileNumber !== 0 ? overrideConsignee.mobileNumber : '',
      contactEmail: overrideConsignee.emailAddress,
    };
  }

  const probillNumber = new URLSearchParams(window.location.search).get('probillNumber');
  formValuesAPI.orderId = probillNumber;

  return axios.post(`${t('ApiEndPoint')}${t('TruckMateApiVirtualFolder')}${endpoints.truckmate.manageOrders.update}`, formValuesAPI).then((res) => {
    if (res.status === 200 || res.status === 201) {
      updateState('data', {
        ...res.data,
        orderNumber: res.data.orderId,
        currencyCode: res.data.totalAmount && res.data.totalAmount.currencyCode,
      }, paymentCallback);
      window.scrollTo(0, 0);
    }
  }).catch((error) => {
    if (error && error.response && error.response.data) {
      const errorMessage = error.response.data.split('\n')[0];
      const sepIndex = errorMessage.indexOf(' ');
      if (sepIndex > 0 && errorMessage.length > sepIndex) {
        throw new SubmissionError({ message: JSON.parse(errorMessage.substring(sepIndex + 1)) });
      }
    }

    throw new SubmissionError({});
  });
};

